import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import config from './config'; // Import our centralized configuration
import apiService from './services/api'; // Import our updated API service

/**
 * Firebase Configuration and Service Integration
 * Enhanced with proper authentication persistence and payment success flow
 *
 * Updated: 2025-06-08 12:05:04 by Mikutzu55
 * Added: Authentication persistence, enhanced logging, and auth state handling
 * Fixed: Duplicate export issue for waitForAuth and getCurrentUser functions
 */

// Your Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth with enhanced persistence
const auth = getAuth(app);

// Set authentication persistence to LOCAL (survives page reloads and redirects)
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    // Auth persistence configured
  })
  .catch((error) => {
    console.error('Firebase: Failed to set auth persistence:', error);
  });

// Initialize other Firebase services
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(
  app,
  process.env.REACT_APP_FIREBASE_REGION || 'us-central1'
);

// Get Firebase region and project ID for API calls
const region = process.env.REACT_APP_FIREBASE_REGION || 'us-central1';
const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;

// API base URL - Use our centralized configuration
const API_BASE_URL = config.API.BACKEND_URL;

// Create authentication providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// Global auth state change logging for debugging
onAuthStateChanged(auth, (user) => {
  if (user) {
    // User authenticated
  } else {
    // No user authenticated
  }
});

// Debug Google provider setup
// Provider initialized successfully

// Initialize user when they authenticate
onAuthStateChanged(auth, async (user) => {
  if (user) {
    const userRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(userRef);

    // Check if the user document already exists
    if (!docSnap.exists()) {
      try {
        // Create a new user document
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          phone: user.phoneNumber || '',
          membership: 'free', // Default membership
          searchLimit: 5, // Free users get 5 searches
          searchesUsed: 0, // No searches used yet
          expirationDate: null, // No expiration date
          paymentId: null, // No payment ID
          lastSearchTime: null, // Track when user last performed a search
          chatHistory: [], // Store chat history with AI
          createdAt: serverTimestamp(), // Account creation date
          lastLogin: serverTimestamp(), // Last login timestamp
          referralCode: user.uid
            .replace(/[^A-Z0-9]/gi, '')
            .substring(0, 8)
            .toUpperCase(),
          referralCredits: 0,
          referralsCompleted: [],
          referredBy: null,
        });
        console.log('Firebase: New user document created for:', user.uid);
      } catch (error) {
        console.error('Firebase: Error creating user document:', error);
      }
    } else {
      // Update last login timestamp and process any deferred referral rewards
      try {
        // Only write lastLogin — sensitive fields (referralCode etc.) are blocked
        // by Firestore rules and must be written by the backend via admin SDK.
        await updateDoc(userRef, { lastLogin: serverTimestamp() });
        // Always call process-pending-referrals on login: backend will backfill
        // referralCode if missing and credit any pending referrals for this user.
        try {
          const idToken = await user.getIdToken();
          const backendUrl = config.API.BACKEND_URL;
          fetch(`${backendUrl}/api/process-pending-referrals`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}` },
          })
            .then((r) => r.json())
            .then((data) => {
              if (data.processed > 0) {
                console.log(
                  `Firebase: Processed ${data.processed} deferred referral reward(s) for ${user.uid}`
                );
              }
            })
            .catch((err) =>
              console.error(
                'Firebase: process-pending-referrals error:',
                err.message
              )
            );
        } catch (pendingErr) {
          console.error(
            'Firebase: Error calling process-pending-referrals:',
            pendingErr.message
          );
        }
        // User login timestamp updated
      } catch (error) {
        console.error('Firebase: Error updating user login timestamp:', error);
      }
    }
  }
});

// Helper function to wait for auth to be ready
export const waitForAuth = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

// Enhanced auth check function
export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        console.log(
          'Firebase: Current user resolved:',
          user ? user.uid : 'none'
        );
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

/**
 * Gets payment details for the current user - now using apiService
 * @returns {Promise<Object>} Payment details including search limits and history
 */
const getPaymentDetails = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be logged in to get payment details');
    }

    const userId = auth.currentUser.uid;
    const idToken = await auth.currentUser.getIdToken();

    console.log('Firebase: Getting payment details for user:', userId);

    // Try the new API service first
    try {
      const result = await apiService.getPaymentDetails(userId, idToken);
      console.log(
        'Firebase: Payment details fetched via API service:',
        result.data
      );
      return result;
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, trying fallback methods:',
        apiError
      );

      // Now proceed with the fallback methods
      const apiErrors = [];

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/payment/details?userId=${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Authorization: `Bearer ${idToken}`,
              Origin: window.location.origin,
            },
            mode: 'cors',
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log(
          'Firebase: Payment details fetched successfully via direct HTTP:',
          data
        );
        return { data };
      } catch (httpError) {
        apiErrors.push({ endpoint: 'Direct HTTP', error: httpError });
        console.warn(
          'Firebase: Direct HTTP call failed, falling back to callable function:',
          httpError
        );

        try {
          const getPaymentDetailsFunc = httpsCallable(
            functions,
            'getPaymentDetails'
          );

          const result = await getPaymentDetailsFunc();
          console.log(
            'Firebase: Payment details fetched successfully via callable function:',
            result
          );
          return result;
        } catch (callableError) {
          apiErrors.push({
            endpoint: 'Callable function',
            error: callableError,
          });
          console.error(
            'Firebase: All payment details API attempts failed:',
            apiErrors
          );
          throw new Error(
            'Failed to get payment details after trying all methods'
          );
        }
      }
    }
  } catch (error) {
    console.error('Firebase: Failed to get payment details:', error);
    throw error;
  }
};

/**
 * Creates a Stripe customer portal session - now using apiService
 * @returns {Promise<{url: string}>} Portal session URL
 */
const createPortalSession = async (portalData = {}) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be logged in to access customer portal');
    }

    console.log(
      'Firebase: Creating portal session for user:',
      auth.currentUser.uid
    );

    const idToken = await auth.currentUser.getIdToken();

    try {
      // Try the new API service first
      const result = await apiService.createPortalSession(portalData, idToken);
      console.log(
        'Firebase: Portal session created via API service:',
        result.data
      );
      return result;
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, falling back to callable function:',
        apiError
      );

      // Fallback to callable function
      const createPortalSessionFunc = httpsCallable(
        functions,
        'createPortalSession'
      );

      const result = await createPortalSessionFunc();
      console.log(
        'Firebase: Portal session created via callable function:',
        result
      );
      return result;
    }
  } catch (error) {
    console.error('Firebase: Failed to create portal session:', error);
    throw error;
  }
};

/**
 * Creates a Stripe Connect Express onboarding link for referral payouts.
 * @returns {Promise<{url: string}>} Stripe-hosted onboarding URL
 */
const createStripeConnectLink = async () => {
  if (!auth.currentUser) throw new Error('User must be logged in');
  const idToken = await auth.currentUser.getIdToken();
  const result = await apiService.createStripeConnectLink(idToken);
  return result;
};

/**
 * Requests a payout of the user's referral credits to their connected
 * Stripe bank account. Minimum payout is $20.
 * @returns {Promise<{success, amount, transferId, message}>}
 */
const requestReferralPayout = async () => {
  if (!auth.currentUser) throw new Error('User must be logged in');
  const idToken = await auth.currentUser.getIdToken();
  const result = await apiService.requestReferralPayout(idToken);
  return result;
};

/**
 * Checks whether the user's Stripe Connect account is set up and ready.
 * @returns {Promise<{connected, payoutsEnabled, chargesEnabled}>}
 */
const getReferralConnectStatus = async () => {
  if (!auth.currentUser) return { connected: false, payoutsEnabled: false };
  const idToken = await auth.currentUser.getIdToken();
  const result = await apiService.getReferralConnectStatus(idToken);
  return result.data;
};

/**
 * Gets the current user's searches used count - now using apiService
 * @returns {Promise<number>} Number of searches used
 */
const getSearchesUsed = async () => {
  try {
    if (!auth.currentUser) return 0;

    const userId = auth.currentUser.uid;
    const idToken = await auth.currentUser.getIdToken();

    // Try the new API service first
    try {
      const result = await apiService.getSearchesUsed(userId, idToken);
      console.log(
        'Firebase: Searches used fetched via API service:',
        result.data
      );
      return result.data.searchesUsed || 0;
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, trying fallback methods:',
        apiError
      );

      // Now proceed with the fallback methods
      const apiErrors = [];

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/user/searches-used?userId=${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Authorization: `Bearer ${idToken}`,
              Origin: window.location.origin,
            },
            mode: 'cors',
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log('Firebase: Searches used fetched via direct HTTP:', data);
        return data.searchesUsed || 0;
      } catch (httpError) {
        apiErrors.push({ endpoint: 'Direct HTTP', error: httpError });
        console.warn(
          'Firebase: Direct HTTP call failed, falling back to callable function:',
          httpError
        );

        try {
          const getSearchesUsedFunc = httpsCallable(
            functions,
            'getSearchesUsed'
          );
          const result = await getSearchesUsedFunc();
          console.log(
            'Firebase: Searches used fetched via callable function:',
            result.data
          );
          return result.data.searchesUsed || 0;
        } catch (callableError) {
          apiErrors.push({
            endpoint: 'Callable function',
            error: callableError,
          });
          console.warn(
            'Firebase: Failed to get searches used from function, using local data:',
            callableError
          );

          try {
            const userDoc = await getDoc(
              doc(db, 'users', auth.currentUser.uid)
            );
            if (userDoc.exists()) {
              return userDoc.data().searchesUsed || 0;
            }
          } catch (firestoreError) {
            apiErrors.push({
              endpoint: 'Firestore direct',
              error: firestoreError,
            });
            console.error(
              'Firebase: All searches used API attempts failed:',
              apiErrors
            );
          }
        }
      }
    }

    return 0;
  } catch (error) {
    console.error('Firebase: Failed to get searches used:', error);
    return 0;
  }
};

/**
 * Gets the user's search limit - now using apiService
 * @returns {Promise<number>} Search limit
 */
const getSearchLimit = async () => {
  try {
    if (!auth.currentUser) return 0;

    const userId = auth.currentUser.uid;
    const idToken = await auth.currentUser.getIdToken();

    // Try the new API service first
    try {
      const result = await apiService.getSearchLimit(userId, idToken);
      console.log(
        'Firebase: Search limit fetched via API service:',
        result.data
      );
      return result.data.searchLimit || 0;
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, trying fallback methods:',
        apiError
      );

      // Now proceed with the fallback methods
      const apiErrors = [];

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/user/search-limit?userId=${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Authorization: `Bearer ${idToken}`,
              Origin: window.location.origin,
            },
            mode: 'cors',
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log('Firebase: Search limit fetched via direct HTTP:', data);
        return data.searchLimit || 0;
      } catch (httpError) {
        apiErrors.push({ endpoint: 'Direct HTTP', error: httpError });
        console.warn(
          'Firebase: Direct HTTP call failed, falling back to callable function:',
          httpError
        );

        try {
          const getSearchLimitFunc = httpsCallable(functions, 'getSearchLimit');
          const result = await getSearchLimitFunc();
          console.log(
            'Firebase: Search limit fetched via callable function:',
            result.data
          );
          return result.data.searchLimit || 0;
        } catch (callableError) {
          apiErrors.push({
            endpoint: 'Callable function',
            error: callableError,
          });
          console.warn(
            'Firebase: Failed to get search limit from function, using local data:',
            callableError
          );

          try {
            const userDoc = await getDoc(
              doc(db, 'users', auth.currentUser.uid)
            );
            if (userDoc.exists()) {
              return userDoc.data().searchLimit || 0;
            }
          } catch (firestoreError) {
            apiErrors.push({
              endpoint: 'Firestore direct',
              error: firestoreError,
            });
            console.error(
              'Firebase: All search limit API attempts failed:',
              apiErrors
            );
          }
        }
      }
    }

    return 0;
  } catch (error) {
    console.error('Firebase: Failed to get search limit:', error);
    return 0;
  }
};

/**
 * Checks if the user has remaining searches available - now using apiService
 * @returns {Promise<Object>} Search credit details including if user can search
 */
const checkSearchCredits = async () => {
  try {
    if (!auth.currentUser) {
      return { canSearch: false, remainingSearches: 0 };
    }

    const userId = auth.currentUser.uid;
    const idToken = await auth.currentUser.getIdToken();

    // Try the new API service first
    try {
      const result = await apiService.checkSearchCredits(userId, idToken);
      console.log(
        'Firebase: Search credits checked via API service:',
        result.data
      );
      const creditInfo = result.data;
      return {
        canSearch: creditInfo.remaining > 0,
        remainingSearches: creditInfo.remaining,
        ...creditInfo,
      };
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, trying fallback methods:',
        apiError
      );

      // Now proceed with the fallback methods
      const apiErrors = [];

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/user/check-credits?userId=${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Authorization: `Bearer ${idToken}`,
              Origin: window.location.origin,
            },
            mode: 'cors',
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error: ${response.status} ${response.statusText}`
          );
        }

        const creditInfo = await response.json();
        console.log(
          'Firebase: Search credits checked via direct HTTP:',
          creditInfo
        );
        return {
          canSearch: creditInfo.remaining > 0,
          remainingSearches: creditInfo.remaining,
          ...creditInfo,
        };
      } catch (httpError) {
        apiErrors.push({ endpoint: 'Direct HTTP', error: httpError });
        console.warn(
          'Firebase: Direct HTTP call failed, falling back to callable function:',
          httpError
        );

        try {
          const checkCreditsFunc = httpsCallable(
            functions,
            'checkSearchCredits'
          );
          const result = await checkCreditsFunc();
          console.log(
            'Firebase: Search credits checked via callable function:',
            result.data
          );
          return result.data;
        } catch (callableError) {
          apiErrors.push({
            endpoint: 'Callable function',
            error: callableError,
          });
          console.error(
            'Firebase: All search credits API attempts failed:',
            apiErrors
          );

          try {
            const userDoc = await getDoc(
              doc(db, 'users', auth.currentUser.uid)
            );
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const searchCredits = userData.searchLimit || 0;
              const searchesUsed = userData.searchesUsed || 0;
              const remaining = Math.max(0, searchCredits - searchesUsed);

              return {
                canSearch: remaining > 0,
                remainingSearches: remaining,
                searchCredits,
                searchesUsed,
                remaining,
              };
            }
          } catch (firestoreError) {
            console.error(
              'Firebase: Even local calculation failed:',
              firestoreError
            );
          }

          return {
            canSearch: false,
            remainingSearches: 0,
            error: 'All API attempts failed',
          };
        }
      }
    }
  } catch (error) {
    console.error('Firebase: Failed to check search credits:', error);
    return { canSearch: false, remainingSearches: 0, error: error.message };
  }
};

/**
 * Helper function to make authenticated HTTP API calls to Firebase Functions
 * @param {string} endpoint The API endpoint to call (without region/project)
 * @param {string} method HTTP method to use (GET, POST, etc.)
 * @param {Object} [body] Request body (for POST/PUT)
 * @returns {Promise<any>} API response data
 */
const callFunctionApi = async (endpoint, method = 'GET', body = null) => {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  const idToken = await auth.currentUser.getIdToken();

  const headers = {
    Authorization: `Bearer ${idToken}`,
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    Origin: window.location.origin,
  };

  const options = {
    method,
    headers,
    credentials: 'same-origin',
    mode: 'cors',
  };

  if (body && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(body);
  }

  console.log(`Firebase: Calling API: ${endpoint} with method ${method}`);

  try {
    // Try both URL formats - direct function and Express API
    let url;
    if (endpoint.startsWith('/')) {
      url = `${API_BASE_URL}${endpoint}`;
    } else {
      url = `${API_BASE_URL}/${endpoint}`;
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      console.error(
        `Firebase: API call failed: ${response.status} ${response.statusText}`
      );
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `API call failed: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(`Firebase: API call to ${endpoint} failed:`, error);
    throw error;
  }
};

/**
 * Creates a checkout session with Stripe
 * @param {Object} data Configuration data for the checkout session
 * @param {string} data.price Stripe price ID
 * @param {string} data.success_url URL to redirect to on successful payment
 * @param {string} data.cancel_url URL to redirect to on canceled payment
 * @param {Object} data.metadata Additional metadata for the checkout session
 * @returns {Promise<{url: string}|{sessionId: string}>} Checkout session data
 */
const createCheckoutSession = async (data) => {
  try {
    console.log('Firebase: Creating checkout session with data:', {
      price: data.price,
      success_url: data.success_url,
      cancel_url: data.cancel_url,
      searchCredits: data.searchCredits || data.metadata?.searchCredits,
      productName: data.productName || data.metadata?.productName,
      membershipType: data.membershipType || data.metadata?.membershipType,
      durationDays: data.durationDays || data.metadata?.durationDays,
      metadata: data.metadata,
    });

    if (!auth.currentUser) {
      throw new Error('User must be logged in to create a checkout session');
    }

    const completeMetadata = {
      searchCredits:
        data.searchCredits?.toString() ||
        data.metadata?.searchCredits?.toString() ||
        '0',
      productName:
        data.productName || data.metadata?.productName || 'VIN Search Credits',
      membershipType:
        data.membershipType || data.metadata?.membershipType || 'free',
      durationDays:
        data.durationDays?.toString() ||
        data.metadata?.durationDays?.toString() ||
        '365',
      ...data.metadata,
    };

    const idToken = await auth.currentUser.getIdToken();

    // ✅ FIXED: Correct paymentData structure
    const paymentData = {
      price: data.price, // ✅ Correct key
      success_url: data.success_url, // ✅ Correct key
      cancel_url: data.cancel_url, // ✅ Correct key
      successUrl: data.success_url, // ✅ Backup key
      cancelUrl: data.cancel_url, // ✅ Backup key
      searchCredits: data.searchCredits || completeMetadata.searchCredits, // ✅ Required
      productName: data.productName || completeMetadata.productName, // ✅ Required
      membershipType: data.membershipType || completeMetadata.membershipType, // ✅ Required
      durationDays: data.durationDays || completeMetadata.durationDays, // ✅ Required
      userId: auth.currentUser.uid, // ✅ For backend
      metadata: completeMetadata, // ✅ For backend
      idToken: idToken, // ✅ Critical for auth
      // Pass referral code from localStorage so the webhook can reward the referrer
      referralCode: localStorage.getItem('referralCode') || undefined,
    };

    // Try the new API service first
    try {
      const result = await apiService.createCheckoutSession(paymentData);
      console.log(
        'Firebase: Checkout session created successfully via API service:',
        result.data
      );
      return result.data;
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, trying fallback methods:',
        apiError
      );

      const apiErrors = [];

      try {
        const requestBody = {
          priceId: data.price,
          price: data.price,
          successUrl: data.success_url,
          cancelUrl: data.cancel_url,
          userId: auth.currentUser.uid,
          metadata: completeMetadata,
          referralCode: localStorage.getItem('referralCode') || undefined,
        };

        const result = await callFunctionApi(
          config.API.ENDPOINTS.PAYMENT.CHECKOUT || 'createCheckoutSession',
          'POST',
          requestBody
        );
        console.log(
          'Firebase: Checkout session created successfully via Express API:',
          result
        );
        return result;
      } catch (error) {
        apiErrors.push({ endpoint: 'Express API', error });
        console.warn(
          'Firebase: Express API call failed, trying direct function endpoint:',
          error
        );

        try {
          const requestBody = {
            priceId: data.price,
            price: data.price,
            successUrl: data.success_url,
            cancelUrl: data.cancel_url,
            userId: auth.currentUser.uid,
            metadata: completeMetadata,
            referralCode: localStorage.getItem('referralCode') || undefined,
          };

          const response = await fetch(
            `${API_BASE_URL}/api/create-checkout-session`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                Authorization: `Bearer ${idToken}`,
                Origin: window.location.origin,
              },
              body: JSON.stringify(requestBody),
              mode: 'cors',
            }
          );

          if (!response.ok) {
            throw new Error(
              `HTTP error: ${response.status} ${response.statusText}`
            );
          }

          const result = await response.json();
          console.log(
            'Firebase: Checkout session created successfully via direct HTTP:',
            result
          );
          return result;
        } catch (httpError) {
          apiErrors.push({ endpoint: 'Direct HTTP', error: httpError });
          console.warn(
            'Firebase: Direct HTTP call failed, falling back to callable function:',
            httpError
          );

          try {
            const checkoutSessionFunc = httpsCallable(
              functions,
              'createCheckoutSession'
            );

            const result = await checkoutSessionFunc({
              price: data.price,
              successUrl: data.success_url,
              cancelUrl: data.cancel_url,
              searchCredits: data.searchCredits || data.metadata?.searchCredits,
              productName: data.productName || data.metadata?.productName,
              membershipType:
                data.membershipType || data.metadata?.membershipType,
              durationDays: data.durationDays || data.metadata?.durationDays,
              metadata: data.metadata || {},
              referralCode: localStorage.getItem('referralCode') || undefined,
            });

            console.log(
              'Firebase: Checkout session created successfully via callable function:',
              result
            );
            return result.data;
          } catch (callableError) {
            apiErrors.push({
              endpoint: 'Callable function',
              error: callableError,
            });
            console.error('Firebase: All API attempts failed:', apiErrors);
            throw new Error(
              'Failed to create checkout session after trying all methods'
            );
          }
        }
      }
    }
  } catch (error) {
    console.error('Firebase: Failed to create checkout session:', error);
    throw error;
  }
};

/**
 * Increments the user's search count and updates last search time
 * @returns {Promise<{searchesUsed: number, searchLimit: number, remainingSearches: number}>} Updated search counts
 */
const incrementSearchCount = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be logged in to increment search count');
    }

    const userId = auth.currentUser.uid;
    const idToken = await auth.currentUser.getIdToken();

    // Try the API service first
    try {
      const result = await apiService.incrementSearchCount(userId, idToken);
      console.log(
        'Firebase: Search count incremented via API service:',
        result.data
      );

      // Update local Firestore for redundancy
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        searchesUsed: result.data.searchesUsed,
        lastSearchTime: serverTimestamp(),
      });

      return result.data;
    } catch (apiError) {
      console.warn(
        'Firebase: API service call failed, falling back to callable function:',
        apiError
      );

      // Fallback to callable function
      const incrementSearchCountFunc = httpsCallable(
        functions,
        'incrementSearchCount'
      );

      const result = await incrementSearchCountFunc();
      console.log(
        'Firebase: Search count incremented via callable function:',
        result.data
      );

      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        searchesUsed: result.data.searchesUsed,
        lastSearchTime: serverTimestamp(),
      });

      return result.data;
    }
  } catch (error) {
    console.error('Firebase: Failed to increment search count:', error);

    if (error.code === 'functions/resource-exhausted') {
      throw new Error(
        'Search limit exceeded. Please purchase more search credits.'
      );
    }

    throw error;
  }
};

/**
 * Checks if the user has active AI chat access
 * @returns {Promise<boolean>} Whether user has active chat access
 */
const hasActiveChatAccess = async () => {
  try {
    if (!auth.currentUser) {
      console.log('🔒 hasActiveChatAccess: No authenticated user');
      return false;
    }

    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (!userDoc.exists()) {
      console.log('🔒 hasActiveChatAccess: User document does not exist');
      return false;
    }

    const userData = userDoc.data();
    console.log('🔍 hasActiveChatAccess: Checking user data', {
      isDemo: userData.isDemo,
      unlimitedAI: userData.unlimitedAI,
      membership: userData.membership,
      email: userData.email,
    });

    // Check if this is a demo user with unlimited AI access
    if (userData.isDemo === true && userData.unlimitedAI === true) {
      console.log(
        '✅ hasActiveChatAccess: Demo user with unlimited AI - GRANTED'
      );
      return true;
    }

    const isPremium =
      userData.membership?.toLowerCase() === 'premium' ||
      userData.membership?.toLowerCase() === 'business';
    if (!isPremium) {
      console.log('🔒 hasActiveChatAccess: Not a premium member - DENIED');
      return false;
    }

    const lastSearchTime = userData.lastSearchTime?.toDate();
    if (!lastSearchTime) {
      console.log('🔒 hasActiveChatAccess: No recent search time - DENIED');
      return false;
    }

    const now = new Date();
    const hoursSinceLastSearch = (now - lastSearchTime) / (1000 * 60 * 60);

    const hasAccess = hoursSinceLastSearch <= 24;
    console.log(
      `${hasAccess ? '✅' : '🔒'} hasActiveChatAccess: Premium user - ${hoursSinceLastSearch.toFixed(1)}h since last search - ${hasAccess ? 'GRANTED' : 'DENIED'}`
    );
    return hasAccess;
  } catch (error) {
    console.error('Firebase: Failed to check chat access status:', error);
    return false;
  }
};

/**
 * Logs a chat interaction with the AI
 * @param {string} userMessage User's message
 * @param {string} aiResponse AI's response
 * @returns {Promise<void>}
 */
const logAIChat = async (userMessage, aiResponse) => {
  try {
    if (!auth.currentUser) return;

    const userRef = doc(db, 'users', auth.currentUser.uid);

    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    const currentChatHistory = userData.chatHistory || [];

    const newChatHistory = [
      {
        timestamp: serverTimestamp(),
        userMessage,
        aiResponse,
      },
      ...currentChatHistory.slice(0, 49),
    ];

    await updateDoc(userRef, {
      chatHistory: newChatHistory,
    });
  } catch (error) {
    console.error('Firebase: Failed to log AI chat:', error);
  }
};

/**
 * Resets user's search count (for development/testing only)
 * @returns {Promise<Object>} Result of the operation
 */
const resetSearchCount = async () => {
  if (process.env.NODE_ENV === 'production') {
    console.warn(
      'Firebase: resetSearchCount should not be called in production'
    );
    return { success: false, message: 'Not allowed in production' };
  }

  try {
    if (!auth.currentUser) {
      throw new Error('User must be logged in to reset search count');
    }

    const resetFunc = httpsCallable(functions, 'resetSearchCount');
    const result = await resetFunc();
    console.log('Firebase: Search count reset:', result.data);
    return result.data;
  } catch (error) {
    console.error('Firebase: Failed to reset search count:', error);
    throw error;
  }
};

/**
 * Debug function to add search credits directly (for testing only)
 */
const debugAddSearchCredits = async (credits = 1) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be logged in');
    }

    const addCreditsFunc = httpsCallable(functions, 'debugAddSearchCredits');
    const result = await addCreditsFunc({ creditsToAdd: credits });
    console.log('Firebase: Debug search credits added:', result.data);
    return result.data;
  } catch (error) {
    console.error('Firebase: Failed to add debug search credits:', error);
    throw error;
  }
};

/**
 * Test function to verify CORS setup is working properly
 * @returns {Promise<Object>} Test results
 */
const testCorsSetup = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Origin: window.location.origin,
      },
      mode: 'cors',
    });

    if (!response.ok) {
      console.error('Firebase: CORS test failed with status:', response.status);
      return { success: false, status: response.status };
    }

    const data = await response.json();
    console.log('Firebase: CORS test successful:', data);
    return { success: true, data };
  } catch (error) {
    console.error('Firebase: CORS test error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Test API endpoint to help diagnose issues
 * @param {string} endpoint Endpoint to test (optional)
 * @returns {Promise<Object>} Test results for multiple endpoints
 */
const testApiEndpoint = async (endpoint = 'health') => {
  try {
    // Use API_BASE_URL from configuration
    const baseUrl = API_BASE_URL;

    const endpoints = [
      `${baseUrl}/${endpoint}`,
      `${baseUrl}/api/${endpoint}`,
      `${baseUrl}/health`,
    ];

    const results = {};

    for (const url of endpoints) {
      try {
        console.log(`Firebase: Testing API endpoint: ${url}`);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Origin: window.location.origin,
            'X-Requested-With': 'XMLHttpRequest',
          },
          mode: 'cors',
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`Firebase: Success for ${url}:`, data);
          results[url] = { success: true, data };
        } else {
          console.warn(
            `Firebase: Failed for ${url}: ${response.status} ${response.statusText}`
          );
          results[url] = {
            success: false,
            status: response.status,
            statusText: response.statusText,
          };
        }
      } catch (error) {
        console.error(`Firebase: Error for ${url}:`, error);
        results[url] = { success: false, error: error.message };
      }
    }

    return results;
  } catch (error) {
    console.error('Firebase: Error testing API endpoints:', error);
    return { success: false, error: error.message };
  }
};

// Export all necessary functions and objects (FIXED - removed duplicate exports)
export {
  app,
  auth,
  db,
  storage,
  functions,
  googleProvider,
  appleProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  createCheckoutSession,
  getPaymentDetails,
  createPortalSession,
  createStripeConnectLink,
  requestReferralPayout,
  getReferralConnectStatus,
  incrementSearchCount,
  getSearchesUsed,
  getSearchLimit,
  checkSearchCredits,
  hasActiveChatAccess,
  logAIChat,
  resetSearchCount,
  debugAddSearchCredits,
  testCorsSetup,
  testApiEndpoint,
  apiService,
};
